/**
 * CounterCool sticky call to action.
 *
 * The card carries a second buy button: stuck to the sidebar column from 1280px
 * up once the shopper scrolls past the sidebar, and in the page flow below that.
 * Below 1280px a bar above the sticky footer repeats the offer, shown only
 * while both the sidebar and the card are off screen. The
 * sticky, the fade and the carousel itself are CSS, in
 * `countercool-sticky-cta.css`. This file adds the five things CSS cannot:
 *
 * - Ends the rail at the bottom of the reviews, in
 *   `--aura-countercool-sticky-cta-rail-bottom`, because CSS cannot measure one element
 *   against another.
 * - Sets `data-visible` when the sidebar leaves the screen, and `data-on-screen`
 *   when the card itself is in view. One observer watches both.
 *   https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver
 * - Stands the bar on the sticky footer, in `--aura-countercool-sticky-cta-bar-bottom`,
 *   because CSS cannot read another element's height.
 * - Drags the carousel from a held mouse button, which no browser does on its
 *   own. https://developer.mozilla.org/en-US/docs/Web/API/Pointer_events
 * - Moves to the next quote every few seconds. A duplicate of the first quote
 *   sits after the last one, so the loop always moves to the right.
 */
(function () {
  const CARD_SELECTOR = '[aura-sticky-cta]';
  const SIDEBAR_SELECTOR = '.aura-section--countercool-sidebar';
  const REVIEWS_SELECTOR = '.aura-section--product-reviews';
  const STICKY_FOOTER_SELECTOR = '.aura-sticky-footer';
  const BAR_SELECTOR = '.aura-countercool-sticky-cta__bar';
  const CAROUSEL_SELECTOR = '[aura-sticky-cta-reviews]';
  const TRACK_SELECTOR = '.aura-countercool-sticky-cta__reviews-track';
  const RAIL_END_PROPERTY = '--aura-countercool-sticky-cta-rail-bottom';
  const BAR_BOTTOM_PROPERTY = '--aura-countercool-sticky-cta-bar-bottom';
  const BAR_HEIGHT_PROPERTY = '--aura-countercool-sticky-cta-bar-height';
  const QUOTE_DELAY = 5000;

  // Calling `checkVisibility` on a browser from before its March 2024 Baseline
  // throws, so the auto advance falls back to the on screen flag alone.
  // https://developer.mozilla.org/en-US/docs/Web/API/Element/checkVisibility
  const canCheckVisibility = typeof Element.prototype.checkVisibility === 'function';

  let quoteTimerId = 0;
  let cardObserver = null;
  let sizeObservers = [];

  /**
   * Watch an element's size and remember the observer, so unload can stop it.
   *
   * @param {Element} targetEl - The element to measure.
   * @param {ResizeObserverCallback} onResize - Runs on every size change.
   * @returns {void}
   */
  const observeSize = (targetEl, onResize) => {
    const observer = new ResizeObserver(onResize);

    observer.observe(targetEl);
    sizeObservers.push(observer);
  };

  /**
   * Wire up the card that is on the page now: the rail end, the visibility flag,
   * the drag and the auto advance.
   *
   * @returns {void}
   */
  const setUpCard = () => {
    const cardEl = document.querySelector(CARD_SELECTOR);
    const sidebarEl = document.querySelector(SIDEBAR_SELECTOR);
    const reviewsEl = document.querySelector(REVIEWS_SELECTOR);
    const stickyFooterEl = document.querySelector(STICKY_FOOTER_SELECTOR);

    // Do not proceed without the card and the sidebar it stands in for.
    if (!cardEl || !sidebarEl) {
      return;
    }

    const barEl = cardEl.querySelector(BAR_SELECTOR);
    const railEl = cardEl.parentElement;

    /**
     * End the rail at the bottom of the reviews.
     *
     * `offsetParent` is the element the rail is positioned against, so measuring
     * both against it turns two screen positions into the one distance the
     * rail's `bottom` needs.
     *
     * @returns {void}
     */
    const setRailEnd = () => {
      // Do not proceed without reviews to measure and a positioned rail.
      if (!reviewsEl || !railEl.offsetParent) {
        return;
      }

      const columnBottom = railEl.offsetParent.getBoundingClientRect().bottom;
      const reviewsBottom = reviewsEl.getBoundingClientRect().bottom;

      railEl.style.setProperty(RAIL_END_PROPERTY, `${columnBottom - reviewsBottom}px`);
    };

    /**
     * Publish the bar's height only while it is shown.
     *
     * The bar stays in the layout when hidden (`visibility: hidden`, not
     * `display: none`), so its `offsetHeight` would otherwise keep reserving
     * space for it even while nothing is drawn there.
     *
     * @returns {void}
     */
    const updateBarHeight = () => {
      // Do not proceed without a bar to measure. It is missing on other
      // sections that render this card without the reviews it stacks.
      if (!barEl) {
        return;
      }

      const barIsVisible = cardEl.dataset.visible === 'true' && cardEl.dataset.onScreen === 'false';

      document.documentElement.style.setProperty(
        BAR_HEIGHT_PROPERTY,
        barIsVisible ? `${barEl.offsetHeight}px` : '0px',
      );
    };

    /**
     * Record what is on screen: the sidebar in `data-visible`, the card in
     * `data-on-screen`.
     *
     * @param {IntersectionObserverEntry[]} entries - One entry per watched element.
     * @returns {void}
     */
    const syncCard = (entries) => {
      entries.forEach((entry) => {
        if (entry.target === cardEl) {
          cardEl.dataset.onScreen = entry.isIntersecting;
          return;
        }

        cardEl.dataset.visible = !entry.isIntersecting;
      });

      // The bar's own box does not resize when it shows or hides, so the
      // ResizeObserver below never catches this. Update it here instead.
      updateBarHeight();
    };

    /**
     * Stand the bar on top of the sticky footer.
     *
     * `offsetHeight` is the laid out height, so it ignores the slide in
     * transform the footer animates with. A rect would include that transform
     * and start the bar a footer's height too low.
     *
     * @returns {void}
     */
    const setBarBottom = () => {
      // Do not proceed without a footer to stand on. It is missing on other
      // templates and zero high from 1280px up, where it is `display: none`.
      // Writing that zero would throw away the fallback height in the CSS.
      if (!stickyFooterEl || !stickyFooterEl.offsetHeight) {
        return;
      }

      cardEl.style.setProperty(BAR_BOTTOM_PROPERTY, `${stickyFooterEl.offsetHeight}px`);
    };

    // One observer watches both, so the two flags cannot disagree about what is
    // on screen. The 1280px and up styles read `data-visible` to fade the card
    // in; the auto advance reads `data-on-screen` at every width.
    cardObserver = new IntersectionObserver(syncCard);

    cardObserver.observe(sidebarEl);
    cardObserver.observe(cardEl);

    setRailEnd();
    setBarBottom();

    // The footer's height changes with the viewport, so the bar is re-stood on
    // it rather than measured once.
    if (stickyFooterEl) {
      observeSize(stickyFooterEl, setBarBottom);
    }

    // Published on the root element, not `cardEl`, so `footer.css` can read it
    // from a different section.
    if (barEl) {
      updateBarHeight();
      observeSize(barEl, updateBarHeight);
    }

    // The reviews widget fills in after load, so the distance above goes stale.
    // Watch the two elements the rail is measured from, not `body`, which left
    // it 43px past the reviews on the first build.
    // https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
    if (reviewsEl && railEl.offsetParent) {
      observeSize(railEl.offsetParent, setRailEnd);
      observeSize(reviewsEl, setRailEnd);
    }

    const carouselEl = cardEl.querySelector(CAROUSEL_SELECTOR);

    // Do not proceed without the carousel. The section drops it with the reviews.
    if (!carouselEl) {
      return;
    }

    let lastPointerX = 0;

    /**
     * Move the carousel by the distance the mouse moved since the last event.
     *
     * @param {PointerEvent} event - The move that followed the press.
     * @returns {void}
     */
    const dragCarousel = (event) => {
      // A drag to the left reads the next quote, so the scroll position grows as
      // the pointer moves back. `movementX` is not reliable across browsers, so
      // the distance comes from the previous screen position.
      carouselEl.scrollLeft += lastPointerX - event.screenX;
      lastPointerX = event.screenX;
    };

    /**
     * Stop the drag and let the carousel settle on a quote again.
     *
     * @param {PointerEvent} event - The release, or the cancelled drag.
     * @returns {void}
     */
    const endDrag = (event) => {
      carouselEl.removeEventListener('pointermove', dragCarousel);
      carouselEl.releasePointerCapture(event.pointerId);

      // Restoring the snap type re-snaps the carousel to the nearest quote. The
      // browser snaps again whenever the snap type changes, so nothing here has
      // to work out which quote is closest or scroll to it.
      // https://drafts.csswg.org/css-scroll-snap/#re-snap
      delete carouselEl.dataset.dragging;
    };

    /**
     * Start a drag when the shopper presses the left mouse button.
     *
     * @param {PointerEvent} event - The press on the carousel.
     * @returns {void}
     */
    const startDrag = (event) => {
      // Do not proceed unless this is the left button of a mouse.
      if (event.pointerType !== 'mouse' || event.button !== 0) {
        return;
      }

      // Otherwise the press selects the quote text, which fights the drag.
      event.preventDefault();

      lastPointerX = event.screenX;

      // The CSS drops the mandatory snapping while this is set, so the strip can
      // follow the pointer.
      carouselEl.dataset.dragging = 'true';

      // Capture keeps the moves coming after the pointer leaves the carousel, so
      // a drag past the card's edge still scrolls.
      // https://developer.mozilla.org/en-US/docs/Web/API/Element/setPointerCapture
      carouselEl.setPointerCapture(event.pointerId);
      carouselEl.addEventListener('pointermove', dragCarousel);
    };

    carouselEl.addEventListener('pointerdown', startDrag);
    carouselEl.addEventListener('pointerup', endDrag);
    carouselEl.addEventListener('pointercancel', endDrag);

    // Do not proceed if the shopper asked for less motion.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const trackEl = carouselEl.querySelector(TRACK_SELECTOR);

    // Do not proceed without two quotes. There is nothing to duplicate or move to.
    if (!trackEl || trackEl.children.length < 2) {
      return;
    }

    // Without a duplicate of the first quote after the last, the wrap would
    // scroll back past every quote. The Hero Slider does the same, see `adr/0004`.
    const duplicateEl = trackEl.firstElementChild.cloneNode(true);

    // Otherwise the quote is read twice and the editor sees two blocks.
    duplicateEl.setAttribute('aria-hidden', 'true');
    duplicateEl.removeAttribute('data-shopify-editor-block');

    trackEl.appendChild(duplicateEl);

    /**
     * Move the carousel to the next quote, wrapping from the last to the first.
     *
     * @returns {void}
     */
    const showNextQuote = () => {
      // `data-on-screen` is the only flag that holds at every width. From 1280px
      // up the card is also faded out until the sidebar leaves, which
      // `checkVisibility` catches through its `visibility: hidden`.
      const cardIsFadedOut =
        canCheckVisibility && !cardEl.checkVisibility({ visibilityProperty: true });
      const shopperCanSeeCard = cardEl.dataset.onScreen === 'true' && !cardIsFadedOut;

      // Do not proceed while nobody can see the carousel.
      if (!shopperCanSeeCard || document.hidden) {
        return;
      }

      // Read at the tick, not tracked: a drag captures the pointer, and a captured
      // pointer sends no leave event to clear a flag.
      const shopperIsOnCarousel =
        carouselEl.matches(':hover, :focus-within') || carouselEl.dataset.dragging;

      // Do not proceed while the shopper is on the carousel (WCAG 2.2.2).
      if (shopperIsOnCarousel) {
        return;
      }

      // The duplicate is the last quote, so it sits at the furthest scroll.
      // Measure it here, not with `offsetLeft`, which counts from the card and
      // lands 21px out.
      const maxScrollLeft = carouselEl.scrollWidth - carouselEl.clientWidth;

      // Rewind from the duplicate to the real first quote, which looks identical.
      if (carouselEl.scrollLeft >= maxScrollLeft - 1) {
        carouselEl.scrollLeft = 0;
      }

      // One quote is exactly one carousel wide, and the CSS snapping settles it.
      carouselEl.scrollTo({
        left: carouselEl.scrollLeft + carouselEl.clientWidth,
        behavior: 'smooth',
      });
    };

    // Every tick re-checks the guards above, so the timer runs on until the card
    // leaves the page.
    quoteTimerId = setInterval(showNextQuote, QUOTE_DELAY);
  };

  setUpCard();

  // The theme editor replaces the section's HTML without running this file
  // again, so the timer and the observer would outlive the card. Shopify asks
  // every section to clean up on unload and to set itself up again on load.
  // https://shopify.dev/docs/storefronts/themes/best-practices/editor/integrate-sections-and-blocks
  document.addEventListener('shopify:section:unload', (event) => {
    // Do not proceed if another section unloaded. Its card is not this one.
    if (!event.target.querySelector(CARD_SELECTOR)) {
      return;
    }

    clearInterval(quoteTimerId);

    sizeObservers.forEach((observer) => observer.disconnect());
    sizeObservers = [];

    // Do not proceed without an observer. The card can unload before one exists.
    if (!cardObserver) {
      return;
    }

    cardObserver.disconnect();
    cardObserver = null;
  });

  document.addEventListener('shopify:section:load', (event) => {
    // Do not proceed if another section loaded. Its card is not this one.
    if (!event.target.querySelector(CARD_SELECTOR)) {
      return;
    }

    setUpCard();
  });
})();
